<template>
  <chart :options="graph" :auto-resize="true"></chart>
</template>

<script>
import EchartsTemplate from 'components/mixins/echarts-template';

import moment from 'moment';
import { each, get } from 'lodash';

import { CHART_COLORS } from 'constants/content-colors';

import 'echarts/lib/chart/line';

export default {
  mixins: [EchartsTemplate],
  data() {
    return {
      group: 'metabolic_rates',
      chart: 'chart-metabolic-rates',
      labels: {
        close: this.$t('label.close'),
        refresh: this.$t('label.refresh'),
        dataView: this.$t('label.data_view'),
        loading: `${this.$t('label.loading')}...`,
        saveAsImage: this.$t('label.save_as_image'),
        derBmrBased: this.$t('label.der_bmr_based'),
        derRmrBased: this.$t('label.der_rmr_based'),
        basalMetabolicRate: this.$t('label.basal_metabolic_rate'),
        restingMetabolicRate: this.$t('label.resting_metabolic_rate'),
        text: `${this.$t(this.chartDetails.section)}: ${this.$t(this.chartDetails.chart)}`,
        graphGeneratedByDietCreator: this.$t('label.graph_generated_by_diet_creator'),
        subtext: `${this.measurementDetails.participant}, ${this.$t('label.time_period')}: ${moment(this.measurementDetails.date[0]).format('DD/MM/YYYY')} - ${moment(this.measurementDetails.date[1]).format('DD/MM/YYYY')}`,
      },
    };
  },
  methods: {
    prepareData() {
      return new Promise((resolve) => {
        const series = [];
        const labels = [];

        // basal_metabolic_rate
        // resting_metabolic_rate
        // der_bmr_based
        // der_rmr_based

        const basalMetabolicRate = {
          name: this.labels.basalMetabolicRate,
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbolSize: 6,
          data: [],
        };

        const restingMetabolicRate = {
          name: this.labels.restingMetabolicRate,
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbolSize: 6,
          data: [],
        };

        const derBmrBased = {
          name: this.labels.derBmrBased,
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbolSize: 6,
          data: [],
        };

        const derRmrBased = {
          name: this.labels.derRmrBased,
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbolSize: 6,
          data: [],
        };

        each(this.measurements, (measurement) => {
          const label = this.formatLabel(measurement);
          labels.push(label);

          const basalMetabolicRateValue = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.basal_metabolic_rate.value') || null;
          const basalMetabolicRateUnit = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.basal_metabolic_rate.expressed_in_unit') || '';
          const basalMetabolicRateName = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.basal_metabolic_rate.name') || null;
          if (basalMetabolicRateValue) {
            this.labels.basalMetabolicRate = basalMetabolicRateName || this.labels.basalMetabolicRate;
            basalMetabolicRate.name = this.labels.basalMetabolicRate;
            this.weightUnit = basalMetabolicRateUnit;
            basalMetabolicRate.data.push({
              name: this.labels.basalMetabolicRate,
              value: basalMetabolicRateValue,
              unit: basalMetabolicRateUnit,
              seq: 2,
            });
          }

          const restingMetabolicRateValue = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.resting_metabolic_rate.value') || null;
          const restingMetabolicRateUnit = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.resting_metabolic_rate.expressed_in_unit') || '';
          const restingMetabolicRateName = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.resting_metabolic_rate.name') || null;
          if (restingMetabolicRateValue) {
            this.labels.restingMetabolicRate = restingMetabolicRateName || this.labels.restingMetabolicRate;
            restingMetabolicRate.name = this.labels.restingMetabolicRate;
            this.weightUnit = restingMetabolicRateUnit;
            restingMetabolicRate.data.push({
              name: this.labels.restingMetabolicRate,
              value: restingMetabolicRateValue,
              unit: restingMetabolicRateUnit,
              seq: 2,
            });
          }

          const derBmrBasedValue = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.der_bmr_based.value') || null;
          const derBmrBasedUnit = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.der_bmr_based.expressed_in_unit') || '';
          const derBmrBasedName = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.der_bmr_based.name') || null;
          if (derBmrBasedValue) {
            this.labels.derBmrBased = derBmrBasedName || this.labels.derBmrBased;
            derBmrBased.name = this.labels.derBmrBased;
            derBmrBased.data.push({
              name: this.labels.derBmrBased,
              value: derBmrBasedValue,
              unit: derBmrBasedUnit,
              seq: 1,
            });
          }

          const derRmrBasedValue = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.der_rmr_based.value') || null;
          const derRmrBasedUnit = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.der_rmr_based.expressed_in_unit') || '';
          const derRmrBasedName = get(measurement, 'MeasurementTypeGroups.metabolic_rates.Measurements.der_rmr_based.name') || null;
          if (derRmrBasedValue) {
            this.labels.derRmrBased = derRmrBasedName || this.labels.derRmrBased;
            derRmrBased.name = this.labels.derRmrBased;
            derRmrBased.data.push({
              name: this.labels.derRmrBased,
              value: derRmrBasedValue,
              unit: derRmrBasedUnit,
              seq: 1,
            });
          }
        });

        if (basalMetabolicRate.data.length > 0) series.push(basalMetabolicRate);
        if (restingMetabolicRate.data.length > 0) series.push(restingMetabolicRate);
        if (derBmrBased.data.length > 0) series.push(derBmrBased);
        if (derRmrBased.data.length > 0) series.push(derRmrBased);

        resolve({ series, labels });
      });
    },
    prepareGraph() {
      this.prepareData().then((data) => {
        const graph = {
          title: {
            text: this.labels.text,
            subtext: this.labels.subtext,
            subtextStyle: {
              color: '#000',
              fontSize: 13,
            },
            top: 10,
            left: '10%',
            padding: 0,
            itemGap: 5,
          },
          toolbox: {
            feature: {
              saveAsImage: {
                show: true,
                title: this.labels.saveAsImage,
              },
              dataView: {
                show: true,
                title: this.labels.dataView,
                lang: [this.labels.dataView, this.labels.close, this.labels.refresh],
                readOnly: true,
              },
            },
            top: 7,
            right: 15,
            itemGap: 15,
          },
          graphic: [
            {
              type: 'text',
              top: 'center',
              right: '20',
              rotation: Math.PI / 2,
              style: {
                text: this.labels.graphGeneratedByDietCreator,
                fill: '#afafaf',
                textVeticalAlign: 'middle',
              },
            },
          ],
          tooltip: {
            trigger: 'axis',
            formatter: this.formatTooltip,
          },
          legend: {
            align: 'right',
            top: 10,
            right: '10%',
            data: [
              { name: this.labels.derBmrBased },
              { name: this.labels.derRmrBased },
              { name: this.labels.basalMetabolicRate },
              { name: this.labels.restingMetabolicRate },
            ],
          },
          color: [
            CHART_COLORS.bmr,
            CHART_COLORS.rmr,
          ],
          xAxis: [
            {
              name: this.labels.measurements,
              nameLocation: 'middle',
              nameGap: 30,
              nameTextStyle: {
                fontWeight: 'bold',
              },
              type: 'category',
              boundaryGap: false,
              axisTick: {
                alignWithLabel: true,
              },
              axisLine: {
                onZero: false,
              },
              splitLine: {
                show: true,
                lineStyle: {
                  type: 'dashed',
                },
              },
              data: data.labels,
            },
          ],
          yAxis: [
            {
              type: 'value',
              name: this.labels.weight,
              nameLocation: 'middle',
              nameGap: 55,
              // nameRotate: 0,
              // min: 0,
              // max: 200,
              position: 'left',
              axisLine: {
                lineStyle: {
                  color: '#000',
                },
              },
              axisLabel: {
                formatter: `{value} ${this.weightUnit}`,
              },
              splitLine: {
                lineStyle: {
                  type: 'dashed',
                },
              },
            },
          ],
          series: data.series,
        };
        this.graph = graph;
      });
    },
  },
};
</script>

<style>
.echarts {
  width: 100% !important;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/professional/persons/participants/participant-evolution/dashboard/charts/chart-metabolic-rates.vue